import RecordView from 'components/RecordView';
import PageSection from 'components/PageSection';
import Spacing from 'components/Spacing';
import ButtonGroup from 'components/ButtonGroup';
import Button from 'components/Button';
import CompanyDetailsForm from './CompanyDetailsForm';
import { useFormContext } from 'react-hook-form';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useGetRole } from 'hooks/ui/useGetRole';
import { FirmSelector } from 'components/FirmSelector';
import { FieldRow } from 'components/FormElements';
import ShareClassDetailsForm from '../ShareClassForm/ShareClassDetailsForm';
import { investee as Investee } from 'further-ui/validations';
import { useState, useEffect } from 'react';
import { Grid } from '@mui/material';

type CompanyDetailsProps = {
  onSuccess: () => void;
  setSelectedCompanyId: (companyId: string) => void;
  setNewShareClassCreated: (newShareClassCreated: boolean) => void;
};

const CompanyDetails: React.FC<CompanyDetailsProps> = ({
  onSuccess,
  setSelectedCompanyId,
  setNewShareClassCreated,
}) => {
  const { handleSubmit, watch, setValue } = useFormContext<
    Investee.NewCompanyFormValues | Investee.NewShareClassFormValues
  >();
  const { isSuperAdmin } = useGetRole();
  const values = watch();

  const isNewCompany = watch('isNewCompany');
  const isNewShareClass = watch('isNewShareClass');
  const legalName = watch('legalName');

  // State to track duplicate company warning
  const [duplicateCompanyWarning, setDuplicateCompanyWarning] = useState<
    string | null
  >(null);

  // Get existing companies for validation
  const { data: existingCompanies } = api.investee.listUniqueInvestees.useQuery(
    { firmId: values.firmId },
    { enabled: !!values.firmId },
  );

  // Custom validation for legal name duplicates using watch
  useEffect(() => {
    if (isNewCompany && legalName && existingCompanies?.result) {
      const existingCompany = existingCompanies.result.find(
        (company) =>
          company.legalName.toLowerCase() === legalName.toLowerCase(),
      );

      if (existingCompany) {
        setDuplicateCompanyWarning(
          `A company with this name already exists and is able to be chosen in the dropdown above.`,
        );
      } else {
        setDuplicateCompanyWarning(null);
      }
    } else {
      setDuplicateCompanyWarning(null);
    }
  }, [isNewCompany, legalName, existingCompanies?.result]);

  const mutationOptions = useHandleTrpcMutation()({
    successMessage: isNewCompany
      ? 'Company created successfully.'
      : isNewShareClass
      ? 'Share class created successfully.'
      : undefined,
    refetchType: 'none',
    onSuccess,
  });

  const createInvesteeMutation =
    api.investee.createInvestee.useMutation(mutationOptions);

  const createShareClassMutation =
    api.investee.createShareClass.useMutation(mutationOptions);

  const onSubmit = async () => {
    if (isNewCompany) {
      const {
        legalName,
        tradingName,
        url,
        twitterUrl,
        linkedinUrl,
        companiesHouseNumber,
        description,
        shareClass,
        nominalShareValue,
        sector,
        stage,
        firmId,
        companyLogo,
      } = values;
      // Prepare the data for the API call
      const investeeData = {
        legalName: legalName ?? '',
        tradingName: tradingName ?? '',
        url,
        twitterUrl,
        linkedinUrl,
        companiesHouseNumber,
        description: description ?? '',
        shareClass: shareClass ?? '',
        nominalShareValue,
        sector: sector ?? '',
        stage: stage ?? '',
        firmId,
        companyLogo,
      };

      // Call the API to create the investee
      const result = await createInvesteeMutation.mutateAsync(investeeData);

      setValue('isNewCompany', false);
      setValue('isNewShareClass', false);
      setValue('investeeId', result._id);
      setValue('shareClassId', result._id);
      setNewShareClassCreated(true);
      setSelectedCompanyId(result._id);
    } else if (isNewShareClass) {
      // Prepare the data for the API call
      const shareClassData = {
        investeeId: values.investeeId ?? '',
        shareClass: values.shareClass ?? '',
        nominalShareValue: values.nominalShareValue ?? '',
      };

      // Call the API to create the share class
      const result = await createShareClassMutation.mutateAsync(shareClassData);

      setValue('isNewCompany', false);
      setValue('isNewShareClass', false);
      setValue('shareClassId', result._id);
      setNewShareClassCreated(true);
      setSelectedCompanyId(result._id);
    } else {
      // An existing share class has been chosen
      // Move to next step without mutation
      setSelectedCompanyId(values.shareClassId ?? '');
      onSuccess();
    }
  };

  const isButtonDisabled =
    (!isNewCompany && !values.investeeId) ||
    (!isNewCompany && !isNewShareClass && !values.shareClassId);

  return (
    <RecordView>
      <Grid size={{ sm: 12, md: 12, lg: 10 }}>
        <Spacing>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Spacing size="md">
              <PageSection title="Company details">
                {isSuperAdmin && (
                  <FieldRow title="Firm*" centerTitle spacing={24}>
                    <FirmSelector
                      placeholder="Select firm"
                      onChange={(firmId) => setValue('firmId', firmId)}
                      firmId={values.firmId}
                      error={!!values.investeeId && !values.firmId}
                    />
                  </FieldRow>
                )}

                <CompanyDetailsForm
                  duplicateCompanyWarning={duplicateCompanyWarning}
                />
              </PageSection>

              {values.investeeId && (
                <PageSection showDivider>
                  <ShareClassDetailsForm />
                </PageSection>
              )}

              <ButtonGroup>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  loading={
                    createInvesteeMutation.isPending ||
                    createShareClassMutation.isPending
                  }
                  disabled={
                    createInvesteeMutation.isPending ||
                    createShareClassMutation.isPending ||
                    isButtonDisabled
                  }
                >
                  Next
                </Button>
              </ButtonGroup>
            </Spacing>
          </form>
        </Spacing>
      </Grid>
    </RecordView>
  );
};

export default CompanyDetails;
